// Make a basic component with Redwing logo at the top and then a block of lorem ipsum

import { Box, Container, Heading, Image, Text } from "@chakra-ui/react";
import usePageMeta from "../../../shared/hooks/usePageMeta";

const Cookies = () => {
  usePageMeta(
    "Cookie Policy - Redwing",
    "How Redwing uses cookies and similar technologies on redwing.app.",
    "/cookie"
  );
  return (
    <Container maxW="container.xl">
      <Image src="/images/logo-512.png" maxH="100px" alt="Redwing Logo" />
      <Box>
        <Heading as="h1" id="cookie-policy" mb={4}>
          COOKIE POLICY
        </Heading>
        <Text mb={4}>
          <strong>Last updated June 28, 2022</strong>
        </Text>
        <Text mb={4}>
          This Cookie Policy explains how Redwing Tech Ltd (&quot;
          <strong>Company</strong>&quot;, &quot;<strong>we</strong>&quot;,
          &quot;<strong>us</strong>&quot;, and &quot;<strong>our</strong>&quot;)
          uses cookies and similar technologies to recognize you when you visit
          our websites at <a href="https://redwing.app">https://redwing.app</a>,
          (&quot;<strong>Websites</strong>&quot;). It explains what these
          technologies are and why we use them, as well as your rights to
          control our use of them.
        </Text>
        <Text mb={4}>
          In some cases we may use cookies to collect personal information, or
          that becomes personal information if we combine it with other
          information.
        </Text>
        <Heading as="h2" mb={4}>
          <strong>What are cookies?</strong>
        </Heading>
        <Text mb={4}>
          Cookies are small data files that are placed on your computer or
          mobile device when you visit a website. Cookies are widely used by
          website owners in order to make their websites work, or to work more
          efficiently, as well as to provide reporting information.
        </Text>
        <Text mb={4}>
          Cookies set by the website owner (in this case, Redwing Tech Ltd) are
          called &quot;first party cookies&quot;. Cookies set by parties other
          than the website owner are called &quot;third party cookies&quot;.
          Third party cookies enable third party features or functionality to be
          provided on or through the website (e.g. like advertising, interactive
          content and analytics). The parties that set these third party cookies
          can recognize your computer both when it visits the website in
          question and also when it visits certain other websites.
        </Text>
        <Heading as="h2" mb={4}>
          <strong>Why do we use cookies?</strong>
        </Heading>
        <Text mb={4}>
          We use first and third party cookies for several reasons. Some cookies
          are required for technical reasons in order for our Websites to
          operate, and we refer to these as &quot;essential&quot; or
          &quot;strictly necessary&quot; cookies. Other cookies also enable us
          to track and target the interests of our users to enhance the
          experience on our Online Properties. Third parties serve cookies
          through our Websites for advertising, analytics and other purposes.
          This is described in more detail below.
        </Text>
        <Text mb={4}>
          The specific types of first and third party cookies served through our
          Websites and the purposes they perform are described below (please
          note that the specific cookies served may vary depending on the
          specific Online Properties you visit):
        </Text>
        <Heading as="h2" mb={4}>
          <strong>How can I control cookies?</strong>
        </Heading>
        <Text mb={4}>
          You have the right to decide whether to accept or reject cookies. You
          can exercise your cookie rights by setting your preferences in the
          Cookie Consent Manager. The Cookie Consent Manager allows you to
          select which categories of cookies you accept or reject. Essential
          cookies cannot be rejected as they are strictly necessary to provide
          you with services.
        </Text>
        <Text mb={4}>
          The Cookie Consent Manager can be found in the notification banner and
          on our website. If you choose to reject cookies, you may still use our
          website though your access to some functionality and areas of our
          website may be restricted. You may also set or amend your web browser
          controls to accept or refuse cookies. As the means by which you can
          refuse cookies through your web browser controls vary from
          browser-to-browser, you should visit your browser&#39;s help menu for
          more information.
        </Text>
        <Text mb={4}>
          In addition, most advertising networks offer you a way to opt out of
          targeted advertising. If you would like to find out more information,
          please visit{" "}
          <a href="http://www.aboutads.info/choices/">
            http://www.aboutads.info/choices/
          </a>{" "}
          or{" "}
          <a href="http://www.youronlinechoices.com">
            http://www.youronlinechoices.com
          </a>
          .
        </Text>
        <Text mb={4}>
          The specific types of first and third party cookies served through our
          Websites and the purposes they perform are described in the table
          below (please note that the specific cookies served may vary depending
          on the specific Online Properties you visit):
        </Text>
        <Heading as="h2" mb={4}>
          <strong>Essential website cookies:</strong>
        </Heading>
        <Text mb={4}>
          These cookies are strictly necessary to provide you with services
          available through our Websites and to use some of its features, such
          as access to secure areas.
        </Text>
        <Text mb={2}>Name: _tlbcpv</Text>
        <Text mb={2}>
          Purpose: Used to record unique visitor views of the consent banner.
        </Text>
        <Text mb={2}>Provider: .termly.io</Text>
        <Text mb={2}>
          Service: Termly{" "}
          <a href="https://termly.io/our-privacy-policy/">
            View Service Privacy Policy
          </a>
        </Text>
        <Text mb={2}>Country: United States</Text>
        <Text mb={2}>Type: http_cookie</Text>
        <Text mb={4}>Expires in: 1 year</Text>
        <Heading as="h2" mb={4}>
          <strong>Performance and functionality cookies:</strong>
        </Heading>
        <Text mb={4}>
          These cookies are used to enhance the performance and functionality of
          our Websites but are non-essential to their use. However, without
          these cookies, certain functionality (like videos) may become
          unavailable.
        </Text>
        <Text mb={2}>Name: _hjAbsoluteSessionInProgress</Text>
        <Text mb={2}>
          Purpose: The cookie is set so Hotjar can track the beginning of the
          user&#39;s journey for a total session count. It does not contain any
          identifiable information.
        </Text>
        <Text mb={2}>Provider: redwing.app</Text>
        <Text mb={2}>
          Service: Hotjar{" "}
          <a href="https://www.hotjar.com/legal/compliance/opt-out">
            View Service Privacy Policy
          </a>
        </Text>
        <Text mb={2}>Country: United States</Text>
        <Text mb={2}>Type: http_cookie</Text>
        <Text mb={4}>Expires in: 30 minutes</Text>
        <Heading as="h2" mb={4}>
          <strong>Analytics and customization cookies:</strong>
        </Heading>
        <Text mb={4}>
          These cookies collect information that is used either in aggregate
          form to help us understand how our Websites are being used or how
          effective our marketing campaigns are, or to help us customize our
          Websites for you.
        </Text>
        <Text mb={2}>Name: _hjRecordingLastActivity</Text>
        <Text mb={2}>
          Purpose: This should be found in sessionStorage (as opposed to
          cookies). This gets updated when a visitor recording starts and when
          data is sent through the WebSocket (the visitor performs an action
          that Hotjar records).
        </Text>
        <Text mb={2}>Provider: redwing.app</Text>
        <Text mb={2}>
          Service: hotjar{" "}
          <a href="https://help.hotjar.com/hc/en-us/articles/115011789248-Hotjar-Cookies">
            View Service Privacy Policy
          </a>
        </Text>
        <Text mb={2}>Country: United States</Text>
        <Text mb={2}>Type: html_session_storage</Text>
        <Text mb={4}>Expires in: session</Text>
        <Text mb={4}>--</Text>
        <Text mb={2}>Name: _ga_#</Text>
        <Text mb={2}>
          Purpose: Used to distinguish individual users by means of designation
          of a randomly generated number as client identifier, which allows
          calculation of visits and sessions
        </Text>
        <Text mb={2}>Provider: .redwing.app</Text>
        <Text mb={2}>
          Service: Google analytics{" "}
          <a href="https://policies.google.com/technologies/ads">
            View Service Privacy Policy
          </a>
        </Text>
        <Text mb={2}>Country: United States</Text>
        <Text mb={2}>Type: http_cookie</Text>
        <Text mb={4}>Expires in: 1 year 11 months 29 days</Text>
        <Text mb={4}>--</Text>
        <Text mb={2}>Name: _ga</Text>
        <Text mb={2}>
          Purpose: It records a particular ID used to come up with data about
          website usage by the user. It is a HTTP cookie that expires after 2
          years.
        </Text>
        <Text mb={2}>Provider: .redwing.app</Text>
        <Text mb={2}>
          Service: Google Analytics{" "}
          <a href="https://policies.google.com/technologies/ads">
            View Service Privacy Policy
          </a>
        </Text>
        <Text mb={2}>Country: United States</Text>
        <Text mb={2}>Type: http_cookie</Text>
        <Text mb={4}>Expires in: 1 year 11 months 29 days</Text>
        <Heading as="h2" mb={4}>
          <strong>Advertising cookies:</strong>
        </Heading>
        <Text mb={4}>
          These cookies are used to make advertising messages more relevant to
          you. They perform functions like preventing the same ad from
          continuously reappearing, ensuring that ads are properly displayed for
          advertisers, and in some cases selecting advertisements that are based
          on your interests.
        </Text>
        <Text mb={2}>Name: _gcl_au</Text>
        <Text mb={2}>
          Purpose: Used by Google AdSense for experimenting with advertisement
          efficiency across websites using their services.
        </Text>
        <Text mb={2}>Provider: .redwing.app</Text>
        <Text mb={2}>
          Service: AdSense{" "}
          <a href="https://policies.google.com/technologies/ads?hl=en">
            View Service Privacy Policy
          </a>
        </Text>
        <Text mb={2}>Country: United States</Text>
        <Text mb={2}>Type: http_cookie</Text>
        <Text mb={4}>Expires in: 2 months 29 days</Text>
        <Text mb={4}>--</Text>
        <Text mb={2}>Name: IDE</Text>
        <Text mb={2}>
          Purpose: Used to measure the conversion rate of ads presented to the
          user. Expires in 1.5 years.
        </Text>
        <Text mb={2}>Provider: .doubleclick.net</Text>
        <Text mb={2}>
          Service: DoubleClick{" "}
          <a href="https://policies.google.com/technologies/ads?hl=en">
            View Service Privacy Policy
          </a>
        </Text>
        <Text mb={2}>Country: United States</Text>
        <Text mb={2}>Type: http_cookie</Text>
        <Text mb={4}>Expires in: 1 year 11 months 29 days</Text>
        <p>
          <strong>
            What about other tracking technologies, like web beacons?
          </strong>
        </p>
        <p>
          Cookies are not the only way to recognize or track visitors to a
          website. We may use other, similar technologies from time to time,
          like web beacons (sometimes called &quot;tracking pixels&quot; or
          &quot;clear gifs&quot;). These are tiny graphics files that contain a
          unique identifier that enable us to recognize when someone has visited
          our Websites or opened an e-mail including them. This allows us, for
          example, to monitor the traffic patterns of users from one page within
          a website to another, to deliver or communicate with cookies, to
          understand whether you have come to the website from an online
          advertisement displayed on a third-party website, to improve site
          performance, and to measure the success of e-mail marketing campaigns.
          In many instances, these technologies are reliant on cookies to
          function properly, and so declining cookies will impair their
          functioning.
        </p>
        <p>
          <strong>Do you use Flash cookies or Local Shared Objects?</strong>
        </p>
        <p>
          Websites may also use so-called &quot;Flash Cookies&quot; (also known
          as Local Shared Objects or &quot;LSOs&quot;) to, among other things,
          collect and store information about your use of our services, fraud
          prevention and for other site operations.
        </p>
        <p>
          If you do not want Flash Cookies stored on your computer, you can
          adjust the settings of your Flash player to block Flash Cookies
          storage using the tools contained in the{" "}
          <a href="http://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager07.html">
            Website Storage Settings Panel
          </a>
          . You can also control Flash Cookies by going to the{" "}
          <a href="http://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager03.html">
            Global Storage Settings Panel
          </a>{" "}
          and following the instructions (which may include instructions that
          explain, for example, how to delete existing Flash Cookies (referred
          to &quot;information&quot; on the Macromedia site), how to prevent
          Flash LSOs from being placed on your computer without your being
          asked, and (for Flash Player 8 and later) how to block Flash Cookies
          that are not being delivered by the operator of the page you are on at
          the time).
        </p>
        <p>
          Please note that setting the Flash Player to restrict or limit
          acceptance of Flash Cookies may reduce or impede the functionality of
          some Flash applications, including, potentially, Flash applications
          used in connection with our services or online content.
        </p>
        <p>
          <strong>Do you serve targeted advertising?</strong>
        </p>
        <p>
          Third parties may serve cookies on your computer or mobile device to
          serve advertising through our Websites. These companies may use
          information about your visits to this and other websites in order to
          provide relevant advertisements about goods and services that you may
          be interested in. They may also employ technology that is used to
          measure the effectiveness of advertisements. This can be accomplished
          by them using cookies or web beacons to collect information about your
          visits to this and other sites in order to provide relevant
          advertisements about goods and services of potential interest to you.
          The information collected through this process does not enable us or
          them to identify your name, contact details or other details that
          directly identify you unless you choose to provide these.
        </p>
        <p>
          <strong>How often will you update this Cookie Policy?</strong>
        </p>
        <p>
          We may update this Cookie Policy from time to time in order to
          reflect, for example, changes to the cookies we use or for other
          operational, legal or regulatory reasons. Please therefore re-visit
          this Cookie Policy regularly to stay informed about our use of cookies
          and related technologies.
        </p>
        <p>
          The date at the top of this Cookie Policy indicates when it was last
          updated.
        </p>
        <p>
          <strong>Where can I get further information?</strong>
        </p>
        <p>
          If you have any questions about our use of cookies or other
          technologies, please email us at contact@redwing.app or by post to:
        </p>
        <p>REDWING TECH LTD</p>
        <p>32 Christiemiller avenue, EH7 6ST</p>
        <p>Phone: +447577248802</p>
      </Box>
    </Container>
  );
};

export default Cookies;
